import { useMemo, useState } from "react";

import { Store } from "custom-types/StoreLocator";
import useBrowserStorage from "hooks/useBrowserStorage";

/**
 * Checks if the current spotlight ids match the previous cycle.
 */
export const arraysMatch = (arr1: number[], arr2: number[]): boolean => {
  if (arr1.length !== arr2.length) {
    return false;
  }

  const sortedArr1 = [...arr1].sort((a, b) => a - b);
  const sortedArr2 = [...arr2].sort((a, b) => a - b);
  return sortedArr1.every((num, index) => num === sortedArr2[index]);
};

/**
 * Randomly shuffles an array of spotlight IDs.
 * Source: https://stackoverflow.com/a/46545530
 */
const shuffleIds = (spotlightIds: number[]): number[] =>
  spotlightIds
    .map((id) => ({ id, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ id }) => id);

/**
 * Custom hook for managing spotlight functionality.
 */
const useSpotlight = (spotlightStores: Store[]) => {
  const [spotlightDismissed, setSpotlightDismissed] = useState(false);
  const [spotlightCycle, setSpotlightCycle] = useBrowserStorage<number[]>(
    "local",
    "spotlightCycle",
    [],
  );

  const onSpotlightDismissed = () => {
    setSpotlightDismissed(true);
  };

  const spotlightStore = useMemo(() => {
    if (!spotlightStores?.length) {
      return null;
    }

    const ids = spotlightStores.map((store) => store.id);
    const shouldReuseCycle = arraysMatch(ids, spotlightCycle);

    // continue with previous cycle if stores haven't changed
    const [selectedId, ...nextIds] = shouldReuseCycle
      ? spotlightCycle
      : shuffleIds(ids);

    setSpotlightCycle([...nextIds, selectedId]);

    return spotlightStores.find((s) => s.id === selectedId) || null;
  }, [spotlightStores]);

  return {
    onSpotlightDismissed,
    spotlightDismissed,
    spotlightStore,
  };
};

export default useSpotlight;
